// SVG graph view: dagre left-to-right layout, pan/zoom, hover tooltips, click-to-pin details,
// highlight + pan-to for findings / machine rows.
import dagre, { type Graph as DagreGraph } from '@dagrejs/dagre';
import type { JsonAudit, JsonEdge, JsonNode } from './serialize';
import { iconSvg } from './icons';
import { HUGE, beltText, clear, effClass, flowLines, flowTotal, fmt, h, itemName, pct, svg } from './util';

export interface GraphViewOptions {
  nodeDetails: (n: JsonNode) => HTMLElement;
  edgeDetails: (e: JsonEdge) => HTMLElement;
}

interface Layout {
  g: DagreGraph;
  width: number;
  height: number;
}

const PREF_KEY = 'sbp-auditor.graph';
function loadPref(name: string, def: boolean): boolean {
  try {
    const v = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}')[name];
    return typeof v === 'boolean' ? v : def;
  } catch {
    return def;
  }
}
function savePref(name: string, value: boolean) {
  try {
    const all = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}');
    all[name] = value;
    localStorage.setItem(PREF_KEY, JSON.stringify(all));
  } catch {}
}

const CHAR_W = 6.3; // approx px per character at the 10.5px label font

function nodeSize(n: JsonNode, a: JsonAudit, vertical = false): { w: number; h: number } {
  switch (n.kind) {
    case 'splitter':
    case 'merger':
    case 'smartSplitter':
      return { w: 40, h: 40 };
    case 'externalIn':
    case 'externalOut': {
      const label = externalLabel(n, a);
      return { w: Math.max(110, label.length * CHAR_W + 34 + (vertical ? 0 : 22)), h: vertical ? 38 : 30 };
    }
    case 'storage':
      return { w: Math.max(150, n.buildingName.length * CHAR_W + 24), h: 56 };
    case 'fluidNetwork':
      return { w: 160, h: 54 };
    case 'machine':
    case 'generator': {
      const lines = machineLines(n);
      const w = Math.max(190, ...lines.map((l) => l.length * CHAR_W + 72));
      return { w: Math.min(w, 320), h: 68 };
    }
    default:
      return { w: Math.max(120, n.buildingName.length * CHAR_W + 20), h: 36 };
  }
}

function externalLabel(n: JsonNode, a: JsonAudit): string {
  if (n.kind === 'externalIn') return `${n.label} · ${n.sushi?.length ? 'sushi belt?' : n.item ? itemName(a, n.item) : '?'}`;
  return n.label;
}

function machineLines(n: JsonNode): string[] {
  const l1 = n.count && n.count > 1 ? `${n.count}× ${n.buildingName}` : n.buildingName;
  let l2: string;
  if (n.kind === 'generator') l2 = `burns ${n.fuelName ?? '?'}${n.powerMW ? ` · ${fmt(n.powerMW)} MW` : ''}`;
  else l2 = n.recipeName ?? 'NO RECIPE';
  const clock = Math.abs(n.clock - 1) > 1e-6 ? ` · clock ${fmt(n.clock * 100)}%` : '';
  const range = n.effRange && n.effRange[1] - n.effRange[0] > 1e-4 ? `${pct(n.effRange[0])}–${pct(n.effRange[1])}` : pct(n.efficiency);
  const l3 = n.paused ? 'PAUSED · switched off' : `${range}${clock}`;
  return [l1, l2, l3];
}

const LINE_H = 13; // text-only label rows
const ICON_PX = 18; // item icon size on belts / pipes
const FLOW_LINE_H = ICON_PX + 2; // rows that carry an icon

/** Items whose flow lines head the label, in the order flowLines() prints them. */
function flowItemsOf(e: JsonEdge): string[] {
  return Object.entries(e.flow)
    .filter(([k, v]) => k !== '*' && v > 0)
    .sort((x, y) => y[1] - x[1])
    .map(([k]) => k);
}

function edgeLabelLines(e: JsonEdge, a: JsonAudit): string[] {
  const lines: string[] = [];
  if (e.passThrough) {
    // Flow is whatever the inputs deliver beyond what the machines take; show the
    // saturated-input number, and only fall back to words when it is unbounded.
    const fl = flowLines(a, e.flow);
    if (fl.length === 0) lines.push('0/min');
    else lines.push(...fl);
    lines.push('(pass-through)');
  } else {
    const fl = flowLines(a, e.flow);
    if (fl.length === 0) lines.push('0/min');
    else lines.push(...fl);
    if (e.backedUp && !e.bottleneck && !e.saturated) lines.push('belt full · backed up');
  }
  if (e.mixed) lines.push('⚠ mixed items (sushi belt)');
  if (e.viaCount !== undefined) {
    // combined view: list the belt tiers actually present, and what was folded
    const tiers = e.segments.map((sg) => shortTier(sg.label)).join(', ');
    lines.push(`${tiers}${e.capacity !== null ? ` · max ${fmt(e.capacity)}/min` : ''}`.trim());
    if (e.viaCount > 0) lines.push(`via ${e.viaCount} splitter${e.viaCount > 1 ? 's' : ''}/merger${e.viaCount > 1 ? 's' : ''}`);
  } else lines.push(beltText(e.limitingSegment ?? e.segments[0]?.label, e.capacity));
  return lines;
}

function shortTier(label: string): string {
  return label.replace('Conveyor Belt', 'Belt').replace('Conveyor Lift', 'Lift').replace('Pipeline', 'Pipe');
}

function edgeClass(e: JsonEdge): string {
  const cls = ['edge'];
  if (e.fluid) cls.push('fluid');
  if (e.bottleneck) cls.push('bottleneck');
  else if (e.saturated) cls.push('saturated');
  else if (e.backedUp) cls.push('backed');
  else if (flowTotal(e.flow) < 1e-9) cls.push('zero');
  if (e.passThrough) cls.push('pass');
  if (e.mixed) cls.push('mixed');
  return cls.join(' ');
}

function markerFor(e: JsonEdge): string {
  if (e.bottleneck) return 'arr-bottleneck';
  if (e.saturated) return 'arr-saturated';
  if (flowTotal(e.flow) < 1e-9) return 'arr-zero';
  if (e.fluid) return 'arr-fluid';
  return 'arr-normal';
}

function strokeWidth(e: JsonEdge): number {
  const total = flowTotal(e.flow);
  if (e.bottleneck) return 5;
  if (total < 1e-9) return 1.2;
  if (e.capacity === null) return 2.5;
  const r = Math.min(1, total / e.capacity);
  return 1.5 + 4 * r;
}

/** Smooth polyline: straight to the first bend, then quadratic curves through the bends. */
function pathFor(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  if (points.length <= 2) return points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  let d = `M${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i]!;
    const n = points[i + 1]!;
    const mx = (p.x + n.x) / 2;
    const my = (p.y + n.y) / 2;
    d += ` Q${p.x.toFixed(1)} ${p.y.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
  }
  const last = points[points.length - 1]!;
  d += ` L${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  return d;
}

export class GraphView {
  readonly root: HTMLElement;
  private svgEl: SVGSVGElement;
  private viewport: SVGGElement;
  private tooltip: HTMLElement;
  private pinned: HTMLElement;
  private legend: HTMLElement;
  private empty: HTMLElement;
  private audit: JsonAudit | null = null;
  private layout: Layout | null = null;
  private nodeEls = new Map<string, SVGGElement>();
  private edgeEls = new Map<string, SVGGElement>();
  /** Edges of the drawn view by id (for marker restore on highlight changes). */
  private drawnEdges = new Map<string, JsonEdge>();
  private tx = 0;
  private ty = 0;
  private k = 1;
  private highlighted: { nodes: Set<string>; edge?: string } | null = null;
  /** Combined view: identical machines merged, splitter/merger chains folded. */
  combined = loadPref('combined', true);
  /** Layout direction: false = left-to-right, true = top-to-bottom. */
  vertical = loadPref('vertical', false);
  private combineBtn: HTMLButtonElement;
  private dirBtn: HTMLButtonElement;

  constructor(private opts: GraphViewOptions) {
    this.combineBtn = h('button', { type: 'button', class: 'toggle', title: 'Merge identical machines and fold splitter/merger chains (C)', onclick: () => this.setCombined(!this.combined) }, '') as HTMLButtonElement;
    this.dirBtn = h('button', { type: 'button', class: 'toggle', title: 'Flow direction: left-to-right or top-to-bottom (V)', onclick: () => this.setVertical(!this.vertical) }, '') as HTMLButtonElement;
    this.syncToggleButtons();
    this.svgEl = svg('svg', { class: 'graph-svg' });
    this.viewport = svg('g', { class: 'viewport' });
    this.svgEl.append(this.defs(), this.viewport);
    this.tooltip = h('div', { class: 'tooltip', hidden: true });
    this.pinned = h('div', { class: 'pinned', hidden: true });
    this.legend = this.buildLegend();
    this.empty = h('div', { class: 'graph-empty', hidden: true });
    const toolbar = h(
      'div',
      { class: 'graph-toolbar' },
      h('button', { type: 'button', onclick: () => this.fit(), title: 'Fit graph to view (F)' }, 'Fit'),
      h('button', { type: 'button', onclick: () => this.zoomBy(1.25), title: 'Zoom in' }, '+'),
      h('button', { type: 'button', onclick: () => this.zoomBy(0.8), title: 'Zoom out' }, '−'),
      h('button', { type: 'button', onclick: () => this.setHighlight(null), title: 'Clear highlight' }, 'Clear'),
      h('button', { type: 'button', onclick: () => (this.legend.hidden = !this.legend.hidden) }, 'Legend'),
      this.combineBtn,
      this.dirBtn,
    );
    this.root = h('div', { class: 'graph' }, this.svgEl, toolbar, this.legend, this.tooltip, this.pinned, this.empty);
    this.bindPanZoom();
    new ResizeObserver(() => {
      if (this.layout && !this.userMoved) this.fit(false);
    }).observe(this.root);
  }

  private userMoved = false;

  private defs(): SVGDefsElement {
    const d = svg('defs');
    for (const id of ['normal', 'bottleneck', 'saturated', 'zero', 'fluid', 'hl']) {
      d.append(
        svg(
          'marker',
          { id: `arr-${id}`, class: `arrow arrow-${id}`, viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 9, markerHeight: 9, markerUnits: 'userSpaceOnUse', orient: 'auto-start-reverse' },
          svg('path', { d: 'M0 0.5 L10 5 L0 9.5 z' }),
        ),
      );
    }
    return d;
  }

  private buildLegend(): HTMLElement {
    const row = (sw: Node, text: string) => h('div', { class: 'legend-row' }, sw, h('span', null, text));
    const line = (cls: string) => {
      const s = svg('svg', { width: 34, height: 12, class: 'legend-line' }, svg('line', { x1: 1, y1: 6, x2: 33, y2: 6, class: cls }));
      return s;
    };
    const box = (cls: string) => h('span', { class: `legend-box ${cls}` });
    return h(
      'div',
      { class: 'legend', hidden: true },
      h('div', { class: 'legend-title' }, 'Legend'),
      row(box('good'), 'machine ≥ 99.9% efficient'),
      row(box('warn'), 'machine 50–99.9%'),
      row(box('bad'), 'machine < 50%'),
      row(box('none'), 'no recipe / other'),
      row(h('span', { class: 'legend-glyph' }, '◁ S'), 'splitter (S* = smart)'),
      row(h('span', { class: 'legend-glyph' }, '▷ M'), 'merger'),
      row(line('leg-normal'), 'belt, width ∝ flow ÷ capacity'),
      row(line('leg-bottleneck'), 'bottleneck: belt too slow'),
      row(line('leg-saturated'), 'saturated: at capacity'),
      row(line('leg-fluid'), 'pipe (fluid)'),
      row(line('leg-zero'), 'no flow'),
      row(line('leg-pass'), 'pass-through (unbounded)'),
    );
  }

  // ---------- data ----------

  /** The node/edge set currently drawn (combined or full). */
  private view(): JsonAudit | null {
    const a = this.audit;
    if (!a) return null;
    if (this.combined && a.collapsed) return { ...a, nodes: a.collapsed.nodes, edges: a.collapsed.edges };
    return a;
  }

  /** Map full-graph ids to the ids drawn in the current view. */
  private mapHighlight(hl: { nodes: Set<string>; edge?: string } | null) {
    if (!hl || !this.combined || !this.audit?.collapsed) return hl;
    const c = this.audit.collapsed;
    const nodes = new Set<string>();
    for (const id of hl.nodes) {
      const m = c.memberOf[id];
      if (m) nodes.add(m);
      else nodes.add(id);
    }
    const edge = hl.edge ? c.edgeOf[hl.edge]?.[0] : undefined;
    // an edge that was folded away: highlight its endpoints instead
    if (hl.edge && edge) {
      const ce = c.edges.find((e) => e.id === edge);
      if (ce) (nodes.add(ce.fromNode), nodes.add(ce.toNode));
    }
    return { nodes, edge };
  }

  setCombined(on: boolean) {
    this.combined = on;
    savePref('combined', on);
    this.syncToggleButtons();
    this.redraw();
  }

  setVertical(on: boolean) {
    this.vertical = on;
    savePref('vertical', on);
    this.syncToggleButtons();
    this.redraw();
  }

  private syncToggleButtons() {
    this.combineBtn.textContent = `Combine: ${this.combined ? 'on' : 'off'}`;
    this.combineBtn.classList.toggle('active', this.combined);
    this.dirBtn.textContent = this.vertical ? 'Flow: ↓ down' : 'Flow: → right';
    this.dirBtn.classList.toggle('active', this.vertical);
  }

  /** Re-layout the current audit keeping the highlight. */
  private redraw() {
    const a = this.audit;
    const hl = this.rawHighlight;
    this.setAudit(a);
    if (hl) this.setHighlight(hl, false);
  }

  private rawHighlight: { nodes: Set<string>; edge?: string } | null = null;

  setAudit(a0: JsonAudit | null) {
    this.audit = a0;
    const a = this.view();
    this.highlighted = null;
    this.pinned.hidden = true;
    this.tooltip.hidden = true;
    clear(this.viewport);
    this.nodeEls.clear();
    this.edgeEls.clear();
    this.drawnEdges.clear();
    this.svgEl.classList.remove('has-hl');
    if (!a || a.nodes.length === 0) {
      this.layout = null;
      this.empty.hidden = false;
      this.empty.textContent = a ? 'Nothing to draw: this blueprint contains no machines, belts or pipes.' : '';
      return;
    }
    this.empty.hidden = true;
    this.layout = this.computeLayout(a);
    this.render(a, this.layout);
    this.userMoved = false;
    this.fit(false);
  }

  private computeLayout(a: JsonAudit): Layout {
    const g = new dagre.graphlib.Graph({ multigraph: true });
    g.setGraph({ rankdir: this.vertical ? 'TB' : 'LR', nodesep: this.vertical ? 40 : 26, ranksep: this.vertical ? 90 : 80, edgesep: 18, marginx: 24, marginy: 24 });
    g.setDefaultEdgeLabel(() => ({}));
    const drawn = new Set<string>();
    for (const n of a.nodes) {
      if (n.kind === 'other' && n.ports.every((p) => !p.edgeId)) continue; // walls etc.: not part of the flow graph
      const { w, h: hh } = nodeSize(n, a, this.vertical);
      g.setNode(n.id, { width: w, height: hh });
      drawn.add(n.id);
    }
    for (const e of a.edges) {
      if (!drawn.has(e.fromNode) || !drawn.has(e.toNode)) continue;
      const lines = edgeLabelLines(e, a);
      const nFlow = flowItemsOf(e).length;
      const width = Math.max(...lines.map((l) => l.length)) * 5.9 + 30;
      g.setEdge(e.fromNode, e.toNode, { width, height: nFlow * FLOW_LINE_H + (lines.length - nFlow) * LINE_H + 6, labelpos: 'c' }, e.id);
    }
    dagre.layout(g);
    const gl = g.graph();
    return { g, width: gl.width ?? 0, height: gl.height ?? 0 };
  }

  private render(a: JsonAudit, lay: Layout) {
    const nodeById = new Map(a.nodes.map((n) => [n.id, n]));
    const edgesLayer = svg('g', { class: 'edges' });
    const labelsLayer = svg('g', { class: 'edge-labels' });
    const nodesLayer = svg('g', { class: 'nodes' });
    this.viewport.append(edgesLayer, nodesLayer, labelsLayer);

    for (const e of a.edges) {
      if (!lay.g.hasNode(e.fromNode) || !lay.g.hasNode(e.toNode)) continue;
      const el = lay.g.edge({ v: e.fromNode, w: e.toNode, name: e.id });
      if (!el?.points) continue;
      const d = pathFor(el.points);
      const grp = svg('g', { class: edgeClass(e), 'data-id': e.id });
      const path = svg('path', { d, class: 'edge-line', 'stroke-width': strokeWidth(e), 'marker-end': `url(#${markerFor(e)})` });
      const hit = svg('path', { d, class: 'edge-hit' });
      grp.append(path, hit);
      edgesLayer.append(grp);
      this.edgeEls.set(e.id, grp);
      this.drawnEdges.set(e.id, e);

      const lines = edgeLabelLines(e, a);
      const lw = el.width ?? 60;
      const lh = el.height ?? 20;
      const lx = el.x ?? 0;
      const ly = el.y ?? 0;
      const lab = svg('g', { class: `elabel ${e.bottleneck ? 'bottleneck' : e.saturated ? 'saturated' : ''}`, 'data-id': e.id });
      lab.append(svg('rect', { x: lx - lw / 2, y: ly - lh / 2, width: lw, height: lh, rx: 3 }));
      // The first N lines are the per-item flows (same order as flowLines), each with its icon.
      const flowItems = flowItemsOf(e);
      let yTop = ly - lh / 2 + 3;
      lines.forEach((t, i) => {
        const isFlowLine = i < flowItems.length;
        const rowH = isFlowLine ? FLOW_LINE_H : LINE_H;
        const ic = isFlowLine ? iconSvg(flowItems[i], lx - lw / 2 + 4, yTop, ICON_PX) : null;
        if (ic) lab.append(ic);
        lab.append(svg('text', { x: lx + (ic ? ICON_PX / 2 + 2 : 0), y: yTop + rowH - 5, class: i === lines.length - 1 ? 'belt' : 'flow' }, t));
        yTop += rowH;
      });
      labelsLayer.append(lab);
      const enter = (ev: MouseEvent) => this.showTip(this.opts.edgeDetails(e), ev);
      const move = (ev: MouseEvent) => this.moveTip(ev);
      const leave = () => this.hideTip();
      const click = (ev: MouseEvent) => {
        ev.stopPropagation();
        this.pin(this.opts.edgeDetails(e));
        this.setHighlight({ nodes: new Set([e.fromNode, e.toNode]), edge: e.id }, false);
      };
      for (const target of [grp, lab]) {
        target.addEventListener('mouseenter', enter);
        target.addEventListener('mousemove', move);
        target.addEventListener('mouseleave', leave);
        target.addEventListener('click', click);
      }
    }

    for (const id of lay.g.nodes()) {
      const n = nodeById.get(id);
      const nl = lay.g.node(id);
      if (!n || nl.x === undefined || nl.y === undefined) continue;
      const grp = this.renderNode(n, a, nl.width, nl.height);
      grp.setAttribute('transform', `translate(${(nl.x - nl.width / 2).toFixed(1)},${(nl.y - nl.height / 2).toFixed(1)})`);
      grp.addEventListener('mouseenter', (ev) => this.showTip(this.opts.nodeDetails(n), ev));
      grp.addEventListener('mousemove', (ev) => this.moveTip(ev));
      grp.addEventListener('mouseleave', () => this.hideTip());
      grp.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.pin(this.opts.nodeDetails(n));
        this.setHighlight({ nodes: new Set([n.id]) }, false);
      });
      nodesLayer.append(grp);
      this.nodeEls.set(n.id, grp);
    }
  }

  private renderNode(n: JsonNode, a: JsonAudit, w: number, hh: number): SVGGElement {
    const g = svg('g', { class: `node kind-${n.kind}`, 'data-id': n.id });
    const text = (x: number, y: number, t: string, cls = '', anchor = 'middle') => svg('text', { x, y, class: cls, 'text-anchor': anchor }, t);
    switch (n.kind) {
      case 'machine':
      case 'generator': {
        const cls = n.paused ? 'none' : effClass(n.efficiency, n.kind === 'generator' || !!n.recipe);
        g.classList.add(`eff-${cls}`);
        if (n.paused) g.classList.add('paused');
        g.append(svg('rect', { x: 0, y: 0, width: w, height: hh, rx: 6, class: 'box' }));
        g.append(svg('path', { d: `M6 0 H7 V${hh} H6 A6 6 0 0 1 0 ${hh - 6} V6 A6 6 0 0 1 6 0 Z`, class: 'band' }));
        g.append(svg('path', { d: `M6 0 H${w - 6} A6 6 0 0 1 ${w} 6 V${hh - 6} A6 6 0 0 1 ${w - 6} ${hh} H6 A6 6 0 0 1 0 ${hh - 6} V6 A6 6 0 0 1 6 0 Z`, class: 'outline' }));
        const [l1, l2, l3] = machineLines(n);
        const ic = iconSvg(n.className, 12, (hh - 40) / 2, 40);
        if (ic) g.append(ic);
        const cx = ic ? (w + 44) / 2 : w / 2 + 3;
        g.append(text(cx, 18, l1!, 'title'));
        g.append(text(cx, 36, l2!, n.recipe || n.kind === 'generator' ? 'sub' : 'sub missing'));
        g.append(text(cx, 55, l3!, `eff eff-${cls}`));
        // product icons in the top-right corner
        const prods = Object.keys(n.production ?? {});
        prods.slice(0, 3).forEach((p, i) => {
          const pi = iconSvg(p, w - 20 - i * 18, 4, 16);
          if (pi) g.append(pi);
        });
        break;
      }
      case 'splitter':
      case 'smartSplitter':
      case 'merger': {
        const isSplit = n.kind !== 'merger';
        // splitter: wide side facing the outputs; merger: wide side facing the inputs
        const pts = this.vertical
          ? isSplit
            ? `${w / 2},2 ${w - 2},${hh - 2} 2,${hh - 2}`
            : `2,2 ${w - 2},2 ${w / 2},${hh - 2}`
          : isSplit
            ? `2,${hh / 2} ${w - 2},2 ${w - 2},${hh - 2}`
            : `2,2 ${w - 2},${hh / 2} 2,${hh - 2}`;
        g.append(svg('polygon', { points: pts, class: 'box' }));
        const letter = n.kind === 'smartSplitter' ? 'S*' : isSplit ? 'S' : 'M';
        const gx = this.vertical ? w / 2 : isSplit ? w * 0.62 : w * 0.38;
        const gy = this.vertical ? (isSplit ? hh * 0.68 : hh * 0.42) + 4 : hh / 2 + 4;
        g.append(text(gx, gy, letter, 'glyph'));
        break;
      }
      case 'externalIn':
      case 'externalOut': {
        const label = externalLabel(n, a);
        let pts: string;
        let tx = w / 2;
        let ty = hh / 2 + 4;
        if (this.vertical) {
          // tags point down: input has an arrow tip at the bottom, output a notch at the top
          pts =
            n.kind === 'externalIn'
              ? `0,0 ${w},0 ${w},${hh - 8} ${w / 2},${hh} 0,${hh - 8}`
              : `0,0 ${w / 2},8 ${w},0 ${w},${hh} 0,${hh}`;
          ty = n.kind === 'externalIn' ? hh / 2 : hh / 2 + 7;
        } else {
          pts =
            n.kind === 'externalIn'
              ? `0,0 ${w - 12},0 ${w},${hh / 2} ${w - 12},${hh} 0,${hh}`
              : `0,0 ${w},0 ${w},${hh} 0,${hh} 12,${hh / 2}`;
          tx = n.kind === 'externalIn' ? (w - 12) / 2 : w / 2 + 6;
        }
        g.append(svg('polygon', { points: pts, class: 'box' }));
        const itemCls = n.kind === 'externalIn' ? n.item : Object.keys(n.supplied)[0];
        const ei = !this.vertical ? iconSvg(itemCls, n.kind === 'externalIn' ? 6 : 16, (hh - 18) / 2, 18) : null;
        if (ei) g.append(ei);
        g.append(text(tx + (ei ? 10 : 0), ty, label, 'title'));
        break;
      }
      case 'storage': {
        const ry = 7;
        g.append(svg('path', { d: `M0 ${ry} A${w / 2} ${ry} 0 0 1 ${w} ${ry} V${hh - ry} A${w / 2} ${ry} 0 0 1 0 ${hh - ry} Z`, class: 'box' }));
        g.append(svg('ellipse', { cx: w / 2, cy: ry, rx: w / 2, ry, class: 'lid' }));
        g.append(text(w / 2, 30, n.buildingName, 'title'));
        g.append(text(w / 2, 45, flowLines(a, n.supplied)[0] ?? 'empty', 'sub'));
        break;
      }
      case 'fluidNetwork': {
        g.append(svg('ellipse', { cx: w / 2, cy: hh / 2, rx: w / 2 - 1, ry: hh / 2 - 1, class: 'box' }));
        g.append(text(w / 2, hh / 2 - 2, 'Pipe network', 'title'));
        g.append(text(w / 2, hh / 2 + 13, flowLines(a, n.supplied).join(', ') || n.label, 'sub'));
        break;
      }
      default: {
        g.append(svg('rect', { x: 0, y: 0, width: w, height: hh, rx: 4, class: 'box' }));
        g.append(text(w / 2, hh / 2 + 4, n.buildingName, 'title'));
      }
    }
    return g;
  }

  // ---------- highlight / navigation ----------

  setHighlight(hl0: { nodes: Set<string>; edge?: string } | null, pan = true) {
    this.rawHighlight = hl0;
    const hl = this.mapHighlight(hl0);
    this.highlighted = hl;
    for (const [id, el] of this.nodeEls) el.classList.toggle('hl', !!hl && hl.nodes.has(id));
    for (const [id, el] of this.edgeEls) {
      const on = !!hl && (hl.edge === id || false);
      el.classList.toggle('hl', on);
      const de = this.drawnEdges.get(id);
      if (de) el.querySelector('.edge-line')?.setAttribute('marker-end', on ? 'url(#arr-hl)' : `url(#${markerFor(de)})`);
    }
    this.viewport.querySelectorAll('.elabel').forEach((l) => l.classList.toggle('hl', !!hl && hl.edge === l.getAttribute('data-id')));
    this.svgEl.classList.toggle('has-hl', !!hl && (hl.nodes.size > 0 || !!hl.edge));
    if (!hl) this.pinned.hidden = true;
    if (hl && pan) this.panToHighlight(hl);
  }

  private panToHighlight(hl: { nodes: Set<string>; edge?: string }) {
    if (!this.layout) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const add = (x: number, y: number) => {
      minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    };
    for (const id of hl.nodes) {
      if (!this.layout.g.hasNode(id)) continue;
      const nl = this.layout.g.node(id);
      if (nl.x === undefined || nl.y === undefined) continue;
      add(nl.x - nl.width / 2, nl.y - nl.height / 2);
      add(nl.x + nl.width / 2, nl.y + nl.height / 2);
    }
    if (hl.edge) {
      const e = this.drawnEdges.get(hl.edge);
      if (e && this.layout.g.hasNode(e.fromNode) && this.layout.g.hasNode(e.toNode)) {
        const el = this.layout.g.edge({ v: e.fromNode, w: e.toNode, name: e.id });
        for (const p of el?.points ?? []) add(p.x, p.y);
      }
    }
    if (!isFinite(minX)) return;
    const W = this.root.clientWidth;
    const H = this.root.clientHeight;
    const bw = maxX - minX + 80;
    const bh = maxY - minY + 80;
    // Keep current zoom unless the target does not fit; never zoom in past 1.5.
    let k = this.k;
    if (bw * k > W || bh * k > H) k = Math.min(W / bw, H / bh);
    k = Math.min(1.5, Math.max(0.1, k));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    this.k = k;
    this.tx = W / 2 - cx * k;
    this.ty = H / 2 - cy * k;
    this.userMoved = true;
    this.applyTransform(true);
  }

  fit(animate = true) {
    if (!this.layout) return;
    const W = this.root.clientWidth || 800;
    const H = this.root.clientHeight || 500;
    // Use the real drawn extent (labels can stick out past dagre's reported size).
    let bx = 0, by = 0, bw = this.layout.width, bh = this.layout.height;
    try {
      const bb = this.viewport.getBBox();
      if (bb.width > 0 && bb.height > 0) (bx = bb.x), (by = bb.y), (bw = bb.width), (bh = bb.height);
    } catch {}
    const k = Math.min(W / (bw + 40), H / (bh + 40), 1.6);
    this.k = Math.max(0.05, k);
    this.tx = (W - bw * this.k) / 2 - bx * this.k;
    this.ty = (H - bh * this.k) / 2 - by * this.k;
    this.userMoved = false;
    this.applyTransform(animate);
  }

  private zoomBy(f: number) {
    const W = this.root.clientWidth;
    const H = this.root.clientHeight;
    this.zoomAt(W / 2, H / 2, f);
  }

  private zoomAt(mx: number, my: number, f: number) {
    const nk = Math.min(6, Math.max(0.05, this.k * f));
    const real = nk / this.k;
    this.tx = mx - (mx - this.tx) * real;
    this.ty = my - (my - this.ty) * real;
    this.k = nk;
    this.userMoved = true;
    this.applyTransform(false);
  }

  private applyTransform(animate: boolean) {
    this.viewport.style.transition = animate ? 'transform 220ms ease-out' : 'none';
    this.viewport.setAttribute('transform', `translate(${this.tx.toFixed(2)},${this.ty.toFixed(2)}) scale(${this.k.toFixed(4)})`);
  }

  private bindPanZoom() {
    const el = this.svgEl;
    el.addEventListener('wheel', (ev) => {
      ev.preventDefault();
      const r = el.getBoundingClientRect();
      const f = Math.exp(-ev.deltaY * (ev.deltaMode === 1 ? 0.05 : 0.0015));
      this.zoomAt(ev.clientX - r.left, ev.clientY - r.top, f);
    }, { passive: false });
    let drag: { x: number; y: number; tx: number; ty: number; moved: boolean } | null = null;
    el.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      drag = { x: ev.clientX, y: ev.clientY, tx: this.tx, ty: this.ty, moved: false };
      el.setPointerCapture(ev.pointerId);
    });
    el.addEventListener('pointermove', (ev) => {
      if (!drag) return;
      const dx = ev.clientX - drag.x;
      const dy = ev.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 4) return;
      drag.moved = true;
      el.classList.add('dragging');
      this.tx = drag.tx + dx;
      this.ty = drag.ty + dy;
      this.userMoved = true;
      this.applyTransform(false);
    });
    const end = (ev: PointerEvent) => {
      if (!drag) return;
      el.releasePointerCapture(ev.pointerId);
      el.classList.remove('dragging');
      drag = null;
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('click', (ev) => {
      // background click (node/edge clicks stop propagation) clears the selection
      if (ev.target === el || (ev.target as Element).classList.contains('viewport')) this.setHighlight(null);
    });
    this.root.tabIndex = 0;
    this.root.addEventListener('keydown', (ev) => {
      if (ev.key === 'f' || ev.key === 'F') this.fit();
      if (ev.key === 'c' || ev.key === 'C') this.setCombined(!this.combined);
      if (ev.key === 'v' || ev.key === 'V') this.setVertical(!this.vertical);
      if (ev.key === 'Escape') this.setHighlight(null);
    });
  }

  // ---------- tooltip / pinned details ----------

  private showTip(content: HTMLElement, ev: MouseEvent) {
    clear(this.tooltip);
    this.tooltip.append(content);
    this.tooltip.hidden = false;
    this.moveTip(ev);
  }
  private moveTip(ev: MouseEvent) {
    if (this.tooltip.hidden) return;
    const r = this.root.getBoundingClientRect();
    const tw = this.tooltip.offsetWidth;
    const th = this.tooltip.offsetHeight;
    let x = ev.clientX - r.left + 16;
    let y = ev.clientY - r.top + 16;
    if (x + tw > r.width - 8) x = Math.max(8, ev.clientX - r.left - tw - 12);
    if (y + th > r.height - 8) y = Math.max(8, r.height - th - 8);
    this.tooltip.style.left = `${x}px`;
    this.tooltip.style.top = `${y}px`;
  }
  private hideTip() {
    this.tooltip.hidden = true;
  }
  private pin(content: HTMLElement) {
    clear(this.pinned);
    this.pinned.append(
      h('button', { type: 'button', class: 'close', title: 'Close', onclick: () => this.setHighlight(null) }, '×'),
      content,
    );
    this.pinned.hidden = false;
    this.hideTip();
  }
}

export { HUGE };
